import React, { useEffect } from "react";
import { Card, Col, Form, Input, InputNumber, message, Modal, Row, Select, Switch, Table, Tooltip, Upload } from "antd";
import i18n from "translations/i18n";
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  DESCRIPTION_TEXT_MAX_CHAR,
  DESCRIPTION_TEXT_ROWS,
  SHORT_DESCRIPTION_TEXT_MAX_CHAR,
  SHORT_DESCRIPTION_TEXT_ROWS,
} from "../../../../constants/InputConstant";
import ImgCrop from "antd-img-crop";
import {
  PRODUCT_COMPANY_GROUPS,
  PRODUCT_GROUPS,
  PRODUCT_IMAGE_ASPECT_RATIO,
  PRODUCT_PACKAGE_SIZES,
  PRODUCT_STOCK_COUNTRIES,
} from "constants/ProductConstant";
import { validProductId } from "../../../../functions/validator";
import { cloneDeep } from "lodash";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import styled from "styled-components";
import dayjs from "dayjs";
import { dummyRequest } from "../../../../functions/browser";
import { useCountries } from "../../../../data/hooks/firestore/useCountries";
import { usePriceLists } from "../../../../data/hooks/firestore/usePriceLists";
import BundleItemsSelector from "./BundledProducts/BundleItemsSelector";
import PackagingMaterialSelector from "./BundledProducts/PackagingMaterialSelector";
import BundlePriceCalculator from "./BundledProducts/BundlePriceCalculator";
import EditablePriceInput from "../../../../components/shared-components/EditablePriceInput";
import { NETHERLANDS_COUNTRY_CODE } from "../../../../constants/PricingConstants";
import { CURRENCY } from "../../../../constants/DeliveryConstants";

const { Option } = Select;

const imageUploadProps = {
  name: "file",
  multiple: true,
  listType: "picture-card",
};

/**
 * When file is uploaded, check for file type and image size
 * @param {*} file The uploaded file
 * @returns Boolean that tells Upload component whether to add image or not
 */
const beforeUpload = (file) => {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    message.error(i18n.t("notifications.only_png_jpg"));
    return Upload.LIST_IGNORE;
  }
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    message.error(i18n.t("notifications.image_max_size"));
    return Upload.LIST_IGNORE;
  }
  return isJpgOrPng && isLt2M;
};

const uploadButton = (
  <div>
    <PlusOutlined />
    <div style={{ marginTop: 8 }}>{i18n.t("upload")}</div>
  </div>
);

const GeneralField = (props) => {
  const isBundle = Form.useWatch("isBundle", props.form);
  const { getCountryPricing, countries } = useCountries();
  const { priceLists } = usePriceLists();
  const price = Form.useWatch("price", props.form);
  const standardPrice = parseFloat(price || 0);

  useEffect(() => {
    if (props?.availableIn) {
      const countriesWithLabels = props.availableIn.map(
        (code) => PRODUCT_STOCK_COUNTRIES.find((country) => country.code === code)?.label,
      );
      props.form.setFieldsValue({ availableIn: countriesWithLabels });
    }
  }, [props.availableIn, props.form]);

  // Auto-fill productGroup when isBundle changes
  useEffect(() => {
    if (isBundle && props.mode === "ADD") {
      props.form.setFieldsValue({ productGroup: "Bundles" });
    } else if (props.mode === "ADD") {
      props.form.setFieldsValue({ productGroup: "" });
    }
  }, [isBundle, props.form]);

  const checkBarcodeUnique = (barcode, currentProductId) => {
    if (!barcode || barcode.trim() === "") return true;

    const duplicateProduct = props.products.find(
      (product) => product.barcode === barcode && product.productId !== currentProductId,
    );

    return !duplicateProduct;
  };

  const rules = {
    productId: [
      {
        required: true,
        message: i18n.t("form.enter_product_id"),
      },
      () => ({
        validator(rule, value) {
          return validProductId(value, props.products, props.productId)
            ? Promise.resolve()
            : Promise.reject(i18n.t("form.product_id_not_unique"));
        },
      }),
    ],
    exactItemCode: [
      {
        required: true,
        message: i18n.t("form.pick_value"),
      },
    ],
    name: [
      {
        required: true,
        message: i18n.t("form.enter_product_name"),
      },
    ],
    description: [
      {
        required: true,
        message: i18n.t("form.enter_description_short"),
      },
    ],
    descriptionLong: [
      {
        required: true,
        message: i18n.t("form.enter_description"),
      },
    ],
    price: [
      {
        required: true,
        message: i18n.t("form.enter_price"),
      },
    ],
    size: [
      {
        required: true,
        message: i18n.t("form.select_package_size"),
      },
    ],
    vat: [
      {
        required: true,
        message: i18n.t("form.enter_vat_rate"),
      },
    ],
    category: [
      {
        required: true,
        message: i18n.t("form.enter_category"),
      },
    ],
    type: [
      {
        required: true,
        message: i18n.t("form.enter_type"),
      },
    ],
    productGroup: [
      {
        required: true,
        message: i18n.t("form.select_product_group"),
      },
    ],
    company: [
      {
        required: true,
        message: i18n.t("form.select_company"),
      },
    ],
  };

  const onDragEnd = ({ source, destination }) => {
    const reorder = (list, startIndex, endIndex) => {
      const [removed] = list.splice(startIndex, 1);
      list.splice(endIndex, 0, removed);

      return list;
    };

    const newFileList =
      !!source && !!destination ? reorder(cloneDeep(props.fileList), source.index, destination.index) : null;
    if (!!newFileList) props.handleUploadOrderChange(newFileList);
  };

  // NL drives the standard price (product.price) via its own plain input; all other
  // countries are optional overrides relative to it, handled here. Bundles have their own copy of
  // this table/logic in BundlePriceCalculator.js, since their "price" field lives in a separate card.
  const handleCountryPriceChange = (countryCode, value) => {
    const fieldName = `countryPrice_${countryCode.toLowerCase()}`;

    if (!value || value === "") {
      props.form.setFieldsValue({ [fieldName]: null });
      return;
    }

    const newPrice = parseFloat(value);

    if (newPrice === standardPrice) {
      props.form.setFieldsValue({ [fieldName]: null });
    } else {
      props.form.setFieldsValue({ [fieldName]: newPrice });
    }
  };

  // NL is always shown - it's the product's standard price field itself, not an override.
  // Other countries only show up once they already have a country price list; the product form
  // never creates one itself, new countries must be added first via the Price Lists page.
  const priceTableData = countries
    .filter(
      (country) =>
        country.countryCode === NETHERLANDS_COUNTRY_CODE ||
        priceLists.some((pl) => pl.type === "country" && pl.countryCode === country.countryCode),
    )
    .map((country) => ({
      key: country.countryCode,
      countryCode: country.countryCode,
      isNetherlands: country.countryCode === NETHERLANDS_COUNTRY_CODE,
    }))
    // NL is the default/standard price, so it always comes first
    .sort((a, b) => (a.isNetherlands ? -1 : b.isNetherlands ? 1 : 0));

  const priceTableColumns = [
    {
      title: i18n.t("country"),
      dataIndex: "countryCode",
      key: "countryCode",
      render: (countryCode, record) => (
        <span>
          {countryCode}
          {record.isNetherlands && (
            <span style={{ marginLeft: 6, fontSize: "11px", color: "#888", fontWeight: "normal" }}>
              ({i18n.t("default")})
            </span>
          )}
        </span>
      ),
    },
    {
      title: i18n.t("price"),
      key: "price",
      width: 160,
      render: (_, record) => {
        const { countryCode, isNetherlands } = record;

        // NL drives the standard price itself, so it's a plain required input, not an "override".
        if (isNetherlands) {
          return (
            <Form.Item name="price" rules={rules.price} noStyle>
              <Input
                type="number"
                step="0.01"
                placeholder={i18n.t("price")}
                prefix={CURRENCY}
                disabled={props.isFieldDisabled}
              />
            </Form.Item>
          );
        }

        const fieldName = `countryPrice_${countryCode.toLowerCase()}`;

        return (
          <>
            <Form.Item name={fieldName} noStyle>
              <input type="hidden" />
            </Form.Item>
            <Form.Item shouldUpdate noStyle>
              {() => (
                <EditablePriceInput
                  record={{ id: countryCode, price: standardPrice }}
                  localCustomPrices={{ [countryCode]: props.form.getFieldValue(fieldName) }}
                  onPriceChange={(_, value) => handleCountryPriceChange(countryCode, value)}
                  disabled={props.isFieldDisabled}
                />
              )}
            </Form.Item>
          </>
        );
      },
    },
  ];

  const vatTableData = countries
    .filter((country) => (country.pricing?.vatRates || []).length > 0)
    .map((country) => ({
      key: country.countryCode,
      countryCode: country.countryCode,
      isNetherlands: country.countryCode === NETHERLANDS_COUNTRY_CODE,
      vatRates: country.pricing.vatRates,
    }))
    // NL first, consistent with the price table
    .sort((a, b) => (a.isNetherlands ? -1 : b.isNetherlands ? 1 : 0));

  const vatTableColumns = [
    {
      title: i18n.t("country"),
      dataIndex: "countryCode",
      key: "countryCode",
    },
    {
      title: i18n.t("vat_rate"),
      key: "vat",
      width: 160,
      render: (_, record) => (
        <Form.Item name={`vat_${record.countryCode.toLowerCase()}`} rules={rules.vat} noStyle>
          <Select
            style={{ width: "100%" }}
            placeholder={i18n.t("form.select_vat_rate")}
            disabled={props.isFieldDisabled}
          >
            {record.vatRates.map((rate) => (
              <Option key={rate} value={rate}>
                {rate}%
              </Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
  ];

  return (
    <>
      <Row gutter={16}>
        <Col xs={24} sm={24} md={17}>
          <Card
            title={
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                {i18n.t("product_info")}{" "}
                <BundleSwitch
                  name="isBundle"
                  label={i18n.t("is_bundle")}
                  valuePropName="checked"
                  tooltip={{ title: i18n.t("form.bundle_tooltip"), icon: <InfoCircleOutlined /> }}
                >
                  <Switch
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                    disabled={props.mode === "EDIT" || props.isFieldDisabled}
                  />
                </BundleSwitch>
              </div>
            }
          >
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item name="productId" label={i18n.t("product_id")} rules={rules.productId}>
                  <Input placeholder={i18n.t("product_id")} disabled={props.mode === "EDIT" || props.isFieldDisabled} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="exactId" label={i18n.t("exact_product_id")} rules={rules.exactItemCode}>
                  <Select
                    style={{ width: "100%" }}
                    placeholder={i18n.t("exact_product_id")}
                    showSearch
                    optionFilterProp="children"
                    optionLabelProp="label"
                    disabled={props.isFieldDisabled}
                  >
                    {props.exactProducts?.map((item) => (
                      <Option key={item.ID} value={item.ID} label={`${item.Description0} (${item.ItemCode})`}>
                        {`${item.Description0} (${item.ItemCode})`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="name" label={i18n.t("product_name")} rules={rules.name}>
                  <Input placeholder={i18n.t("name")} disabled={props.isFieldDisabled} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="shortDescription" label={i18n.t("description_short")} rules={rules.description}>
                  <Input.TextArea
                    rows={SHORT_DESCRIPTION_TEXT_ROWS}
                    showCount
                    maxLength={SHORT_DESCRIPTION_TEXT_MAX_CHAR}
                    placeholder={i18n.t("description_short")}
                    disabled={props.isFieldDisabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="description"
                  label={i18n.t("description")}
                  tooltip={{ title: i18n.t("form.description_tooltip"), icon: <InfoCircleOutlined /> }}
                  rules={rules.description}
                >
                  <Input.TextArea
                    rows={DESCRIPTION_TEXT_ROWS}
                    showCount
                    maxLength={DESCRIPTION_TEXT_MAX_CHAR}
                    placeholder={i18n.t("description")}
                    disabled={props.isFieldDisabled}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <BundleItemsSelector form={props.form} products={props.products} disabled={props.isFieldDisabled} />

          <PackagingMaterialSelector form={props.form} products={props.products} disabled={props.isFieldDisabled} />

          <BundlePriceCalculator form={props.form} products={props.products} disabled={props.isFieldDisabled} />

          {isBundle && (
            <Card title={i18n.t("desadv_logistics")}>
              <Row gutter={16}>
                <Col xs={12} md={6}>
                  <Form.Item
                    name={"cratesPerLayer"}
                    label={i18n.t("crates_per_layer")}
                    tooltip={{
                      title: i18n.t("form.crates_per_layer_tooltip"),
                      icon: <InfoCircleOutlined />,
                    }}
                  >
                    <InputNumber
                      placeholder={i18n.t("form.enter_crates_per_layer")}
                      style={{ width: "100%" }}
                      min={1}
                      precision={0}
                      disabled={props.isFieldDisabled}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          <Card title={i18n.t("product_details")}>
            <Row gutter={16}>
              {!isBundle && (
                <Col xs={12} md={6}>
                  <Form.Item name={"weight"} label={i18n.t("weight")}>
                    <InputNumber
                      placeholder={i18n.t("form.enter_weight_in_grams")}
                      style={{ width: "100%" }}
                      disabled={props.isFieldDisabled}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={12} md={6}>
                <Form.Item name={"shelfLife"} label={i18n.t("shelf_life")}>
                  <InputNumber
                    placeholder={i18n.t("form.enter_shelf_life")}
                    style={{ width: "100%" }}
                    disabled={props.isFieldDisabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item name={"internalShelfLife"} label={i18n.t("internal_shelf_life")}>
                  <InputNumber
                    placeholder={i18n.t("form.enter_internal_shelf_life")}
                    style={{ width: "100%" }}
                    disabled={props.isFieldDisabled}
                  />
                </Form.Item>
              </Col>
              {!isBundle && (
                <Col xs={12} md={6}>
                  <Form.Item name="size" label={i18n.t("package_size")} rules={rules.size}>
                    <Select
                      mode="single"
                      style={{ width: "100%" }}
                      placeholder={i18n.t("form.select_package_size")}
                      notFoundContent={<span>{i18n.t("table_no_types")}</span>}
                      disabled={props.isFieldDisabled}
                    >
                      {PRODUCT_PACKAGE_SIZES.map((size) => (
                        <Option key={size} value={size}>
                          {i18n.t(size)}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
              <Col xs={12}>
                <Form.Item
                  name={"barcode"}
                  label={i18n.t("barcode")}
                  rules={[
                    {
                      pattern: /^[0-9]{8,13}$/,
                      message: i18n.t("form.enter_valid_barcode"),
                    },
                    () => ({
                      validator(rule, value) {
                        if (!value || value.trim() === "") {
                          return Promise.resolve();
                        }

                        const isUnique = checkBarcodeUnique(value, props.productId);

                        if (isUnique) {
                          return Promise.resolve();
                        } else {
                          const duplicateProduct = props.products.find(
                            (product) => product.barcode === value && product.productId !== props.productId,
                          );
                          return Promise.reject(
                            i18n.t("notifications.barcode_already_exists") +
                              (duplicateProduct ? ` ${duplicateProduct.productName}` : ""),
                          );
                        }
                      },
                    }),
                  ]}
                >
                  <Input
                    placeholder={i18n.t("form.enter_barcode")}
                    style={{ width: "100%" }}
                    maxLength={13}
                    disabled={props.isFieldDisabled}
                  />
                </Form.Item>
              </Col>
            </Row>

            {!isBundle && (
              <Row gutter={16} style={{ marginTop: 24 }}>
                <Col xs={24} md={12}>
                  <VatSectionTitle>
                    {i18n.t("price_per_country")}{" "}
                    <Tooltip title={i18n.t("form.price_per_country_tooltip")}>
                      <InfoCircleOutlined style={{ fontSize: 13, color: "#888" }} />
                    </Tooltip>
                  </VatSectionTitle>
                  <Table columns={priceTableColumns} dataSource={priceTableData} pagination={false} size="small" />
                </Col>
                <Col xs={24} md={12} className="mt-4 mt-md-0">
                  <VatSectionTitle>{i18n.t("vat_rate")}</VatSectionTitle>
                  <Table columns={vatTableColumns} dataSource={vatTableData} pagination={false} size="small" />
                </Col>
              </Row>
            )}
          </Card>
          <Card title={i18n.t("availability")}>
            <Row gutter={16}>
              <Col xs={12}>
                <Form.Item name="productInStock" label={i18n.t("inStock")} valuePropName="checked">
                  <Switch checkedChildren="Yes" unCheckedChildren="No" disabled={props.isFieldDisabled} />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item name="isVisible" label={i18n.t("visible_in_app")} valuePropName="checked">
                  <Switch
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                    defaultChecked
                    disabled={props.isFieldDisabled}
                  />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item name="productInSeason" label={i18n.t("inSeason")} valuePropName="checked">
                  <Switch
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                    defaultChecked
                    disabled={props.isFieldDisabled}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="availableIn"
              tooltip={{ title: i18n.t("form.product_stock_tooltip"), icon: <InfoCircleOutlined /> }}
              label={i18n.t("availability_per_country")}
            >
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder={i18n.t("form.enter_country")}
                notFoundContent={<span>{i18n.t("table_no_countries")}</span>}
                disabled={props.isFieldDisabled}
              >
                {PRODUCT_STOCK_COUNTRIES.map((country) => (
                  <Option key={country.code} value={country.code}>
                    {i18n.t(country.label)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={7}>
          <Card title={i18n.t("media")}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable" direction="horizontal">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <ImgCrop
                      modalWidth={1000}
                      minZoom={0}
                      modalTitle={i18n.t("crop_image")}
                      modalOk={i18n.t("add")}
                      modalCancel={i18n.t("close")}
                      aspect={PRODUCT_IMAGE_ASPECT_RATIO}
                      cropperProps={{
                        restrictPosition: false,
                      }}
                    >
                      <StyledUpload
                        {...imageUploadProps}
                        fileList={props.fileList}
                        beforeUpload={beforeUpload}
                        onChange={(e) => props.handleUploadChange(e)}
                        onPreview={(e) => props.handlePreview(e)}
                        customRequest={dummyRequest}
                        maxCount={4}
                        multiple={false}
                        disabled={props.isFieldDisabled}
                        itemRender={(originNode, file, fileList) => (
                          <Draggable key={file.uid} draggableId={file.uid} index={fileList.indexOf(file)}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  cursor: "move",
                                  height: "auto",
                                }}
                              >
                                {originNode}
                              </div>
                            )}
                          </Draggable>
                        )}
                      >
                        {props.fileList.length >= 4 ? null : uploadButton}
                      </StyledUpload>
                    </ImgCrop>
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            {/*<ImgCrop*/}
            {/*  modalWidth={1000}*/}
            {/*  minZoom={0}*/}
            {/*  modalTitle={i18n.t("crop_image")}*/}
            {/*  modalOk={i18n.t("add")}*/}
            {/*  modalCancel={i18n.t("close")}*/}
            {/*  aspect={PRODUCT_IMAGE_ASPECT_RATIO}*/}
            {/*  cropperProps={{*/}
            {/*    restrictPosition: false,*/}
            {/*  }}*/}
            {/*>*/}
            {/*  <Upload*/}
            {/*    {...imageUploadProps}*/}
            {/*    fileList={props.fileList}*/}
            {/*    beforeUpload={beforeUpload}*/}
            {/*    onChange={(e) => props.handleUploadChange(e)}*/}
            {/*    onPreview={(e) => props.handlePreview(e)}*/}
            {/*    customRequest={dummyRequest}*/}
            {/*    maxCount={4}*/}
            {/*    multiple={false}*/}
            {/*  >*/}
            {/*    {props.fileList.length >= 4 ? null : uploadButton}*/}
            {/*  </Upload>*/}
            {/*</ImgCrop>*/}
            {/*{props.fileList && (*/}
            {/*  <DragDropContext onDragEnd={onDragEnd}>*/}
            {/*    <Droppable droppableId="droppable">*/}
            {/*      {(provided) => (*/}
            {/*        <div ref={provided.innerRef} {...provided.droppableProps}>*/}
            {/*          {props.fileList.map((item, index) => (*/}
            {/*            <Draggable key={item.uid} draggableId={item.uid} index={index}>*/}
            {/*              {(provided) => (*/}
            {/*                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>*/}
            {/*                  <UploadList*/}
            {/*                    showDownloadIcon={false}*/}
            {/*                    showRemoveIcon={false}*/}
            {/*                    listType={props.listType}*/}
            {/*                    onPreview={props.handlePreview}*/}
            {/*                    items={[item]}*/}
            {/*                    locale={"nl_NL"}*/}
            {/*                  />*/}
            {/*                </div>*/}
            {/*              )}*/}
            {/*            </Draggable>*/}
            {/*          ))}*/}
            {/*          {provided.placeholder}*/}
            {/*        </div>*/}
            {/*      )}*/}
            {/*    </Droppable>*/}
            {/*  </DragDropContext>*/}
            {/*)}*/}
            {props.showReorderNotification && (
              <ReorderNotification>{i18n.t("reorderImagesNotification")}</ReorderNotification>
            )}
            <Modal
              open={props.previewVisible}
              title={props.previewTitle}
              footer={null}
              onCancel={props.handlePreviewCancel}
            >
              <img alt="example" style={{ width: "100%" }} src={props.previewImage} />
            </Modal>
          </Card>
          <Card title={i18n.t("product_of")}>
            <Form.Item name="company" label={i18n.t("company")} rules={rules.company}>
              <Select
                style={{ width: "100%" }}
                placeholder={i18n.t("choose_a_company")}
                notFoundContent={<span>{i18n.t("table_no_companies")}</span>}
                disabled={props.isFieldDisabled}
              >
                {PRODUCT_COMPANY_GROUPS.map((company) => (
                  <Option key={company.value} value={company.value}>
                    {company.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Card>
          <Card title={i18n.t("category")}>
            <Form.Item name="productGroup" label={i18n.t("product_group")} rules={rules.productGroup}>
              <Select
                style={{ width: "100%" }}
                placeholder={i18n.t("form.select_product_group")}
                notFoundContent={<span>{i18n.t("table_no_groups")}</span>}
                disabled={isBundle || props.isFieldDisabled}
              >
                {PRODUCT_GROUPS.filter((group) => group.label !== "bundles").map((group) => (
                  <Option key={group.value} value={group.value}>
                    {i18n.t(group.label)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {!isBundle && (
              <Form.Item name="category" label={i18n.t("categories")} rules={rules.category}>
                <Select
                  style={{ width: "100%" }}
                  placeholder={i18n.t("form.enter_category")}
                  notFoundContent={<span>{i18n.t("table_no_categories")}</span>}
                  disabled={props.isFieldDisabled}
                >
                  {props.categories.map((category) => (
                    <Option key={category}>{category}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Card>
          {!isBundle && (
            <Card title={i18n.t("types")}>
              <Form.Item name="attributes" label={i18n.t("type")} rules={rules.type}>
                <Select
                  mode="tags"
                  style={{ width: "100%" }}
                  placeholder={i18n.t("form.enter_type")}
                  notFoundContent={<span>{i18n.t("table_no_types")}</span>}
                  disabled={props.isFieldDisabled}
                >
                  {!!props.attributes &&
                    props.attributes.map((attribute) => !!attribute && <Option key={attribute}>{attribute}</Option>)}
                </Select>
              </Form.Item>
            </Card>
          )}
          <div>
            {props.createdAt && props.createdBy && (
              <UpdateInfo>
                <InfoCircleOutlined className="icon" />
                Created by {props.createdBy} {dayjs.unix(props.createdAt).format("HH:mm:ss")}{" "}
                {dayjs.unix(props.createdAt).format("DD MMMM YYYY")}
              </UpdateInfo>
            )}
            {props.updatedAt && props.updatedBy && (
              <UpdateInfo>
                <InfoCircleOutlined className="icon" />
                Last updated by {props.updatedBy} {""}
                {dayjs.unix(props.updatedAt).format("HH:mm:ss")} {dayjs.unix(props.updatedAt).format("DD MMMM YYYY")}
              </UpdateInfo>
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

const BundleSwitch = styled(Form.Item)`
  margin-bottom: 0;

  .ant-row {
    display: flex;
    flex-direction: row !important;
    align-items: center;
    justify-content: center;

    .ant-form-item-label {
      padding: 0 !important;
    }

    .ant-form-item-control {
      width: auto !important;
    }
  }
`;

const StyledUpload = styled(Upload)`
  .ant-upload-list-item-container {
    height: auto !important;
  }
`;

const ReorderNotification = styled.p`
  color: #ff2020;
  font-weight: 500;
  margin-bottom: 0;
`;

const UpdateInfo = styled.span`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #888;
  margin-top: 8px;

  .icon {
    margin-right: 8px;
    color: #1890ff;
  }
`;

const VatSectionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 16px;
`;

export default GeneralField;
